import { useEffect, useState } from "react";
import { nowPositionY } from "@/lib/agenda-time";
import { useIsMobile } from "@/lib/use-media-query";

type Props = {
  startHour: string;
  endHour: string;
  /** Cheia (dia atual) ou fantasma (outros dias quando mode="all"). */
  variant?: "full" | "ghost";
  /** Linha sólida ou pontilhada. */
  lineStyle?: "solid" | "dashed";
  /** Cor principal da linha (hex). */
  color?: string;
  /** Se true, destaca dia atual com traço mais grosso + dot. Se false, todas iguais. */
  highlightToday?: boolean;
};

/**
 * Linha do horário atual.
 * Atualiza a cada 30s. Retorna null se o horário atual está fora do range.
 */
export function CurrentTimeIndicator({
  startHour,
  endHour,
  variant = "full",
  lineStyle = "solid",
  color = "#F43F5E",
  highlightToday = true,
}: Props) {
  const [now, setNow] = useState(() => new Date());
  const isMobile = useIsMobile();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const y = nowPositionY(startHour, endHour, now);
  if (y === null) return null;

  // Quando highlight desligado → todas as colunas como "ghost"
  const effectiveFull = variant === "full" && highlightToday;
  const isDashed = lineStyle === "dashed";

  // Pattern do dashed — mais singelo no mobile
  const dashSize = isMobile ? 3 : 5;
  const gapSize = isMobile ? 3 : 4;

  return (
    <div
      data-now-line
      className="pointer-events-none absolute left-0 right-0 z-20 transition-opacity duration-100"
      style={{ top: y }}
      aria-hidden
    >
      {effectiveFull && (
        <div
          className="absolute -left-1 top-[-4px] h-2 w-2 rounded-full shadow-[0_0_0_2px_white]"
          style={{ background: color }}
        />
      )}
      {isDashed ? (
        <div
          style={{
            height: 1,
            backgroundImage: `linear-gradient(to right, ${effectiveFull ? color : `${color}80`} 50%, transparent 50%)`,
            backgroundSize: `${dashSize + gapSize}px 1px`,
            backgroundRepeat: "repeat-x",
          }}
        />
      ) : (
        <div
          style={{
            height: effectiveFull ? 2 : 1,
            background: effectiveFull ? color : `${color}66`,
          }}
        />
      )}
    </div>
  );
}
